import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ChakraSelect,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@elkaso-app/web-design';
import { useCreateSourceBrandLocationAttributeApi } from 'apis/crm-source-brand-location-attributes/use-create-source-brand-location-attribute-api';
import { useDeleteSourceBrandLocationAttributeApi } from 'apis/crm-source-brand-location-attributes/use-delete-source-brand-location-attribute-api';
import { useGetAttributesByLocationApi } from 'apis/crm-source-brand-location-attributes/use-get-attributes-by-location-api';
import { useUpdateSourceBrandLocationAttributeApi } from 'apis/crm-source-brand-location-attributes/use-update-source-brand-location-attribute-api';
import React, { useState } from 'react';
import { FiEdit3, FiPlus, FiTrash2 } from 'react-icons/fi';

interface LocationAttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: number;
}

interface AttributeFormData {
  key: string;
  value: string;
  valueCast: 'string' | 'number' | 'boolean' | 'json';
}

type ValueTypeOption = {
  value: 'string' | 'number' | 'boolean' | 'json';
  label: string;
  description: string;
};

const LocationAttributesModal: React.FC<LocationAttributesModalProps> = ({ isOpen, onClose, locationId }) => {
  const toast = useToast();

  // Value type options for ChakraSelect
  const valueTypeOptions: ValueTypeOption[] = [
    {
      value: 'string',
      label: 'String',
      description: 'Text value (e.g., "Hello World")',
    },
    {
      value: 'number',
      label: 'Number',
      description: 'Numeric value (e.g., 42, 3.14)',
    },
    {
      value: 'boolean',
      label: 'Boolean',
      description: 'True or false value',
    },
    {
      value: 'json',
      label: 'JSON',
      description: 'Structured data (e.g., {"key": "value"})',
    },
  ];

  // API hooks
  const { data: attributesData, isLoading, refetch } = useGetAttributesByLocationApi(locationId);
  const createMutation = useCreateSourceBrandLocationAttributeApi();
  const updateMutation = useUpdateSourceBrandLocationAttributeApi();
  const deleteMutation = useDeleteSourceBrandLocationAttributeApi();

  // Form state
  const [formData, setFormData] = useState<AttributeFormData>({
    key: '',
    value: '',
    valueCast: 'string',
  });
  const [editingAttributeId, setEditingAttributeId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Partial<AttributeFormData>>({});

  // Modal controls for delete confirmation
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [attributeToDelete, setAttributeToDelete] = useState<number | null>(null);

  const validateForm = (): boolean => {
    const newErrors: Partial<AttributeFormData> = {};

    if (!formData.key.trim()) newErrors.key = 'Key is required';
    if (!formData.value.trim()) newErrors.value = 'Value is required';

    // Validate value based on type
    if (formData.valueCast === 'number' && isNaN(Number(formData.value))) {
      newErrors.value = 'Value must be a valid number';
    }
    if (formData.valueCast === 'boolean' && !['true', 'false'].includes(formData.value.toLowerCase())) {
      newErrors.value = 'Value must be true or false';
    }
    if (formData.valueCast === 'json') {
      try {
        JSON.parse(formData.value);
      } catch {
        newErrors.value = 'Value must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the validation errors',
        status: 'error',
      });
      return;
    }

    try {
      if (editingAttributeId) {
        // Update existing attribute
        await updateMutation.mutateAsync({
          id: editingAttributeId,
          data: {
            key: formData.key,
            value: formData.value,
            valueCast: formData.valueCast,
          },
        });
      } else {
        // Create new attribute
        await createMutation.mutateAsync({
          sourceBrandLocationId: locationId,
          key: formData.key,
          value: formData.value,
          valueCast: formData.valueCast,
        });
      }

      resetForm();
      refetch();
    } catch (error) {
      // Error is handled in the API hook
    }
  };

  const resetForm = () => {
    setFormData({
      key: '',
      value: '',
      valueCast: 'string',
    });
    setEditingAttributeId(null);
    setErrors({});
  };

  const handleEdit = (attribute: any) => {
    setFormData({
      key: attribute.key,
      value: typeof attribute.value === 'object' ? JSON.stringify(attribute.value) : String(attribute.value),
      valueCast: 'string', // Default, since we don't get the original cast type
    });
    setEditingAttributeId(attribute.id);
  };

  const handleDelete = (attributeId: number) => {
    setAttributeToDelete(attributeId);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    if (attributeToDelete) {
      try {
        await deleteMutation.mutateAsync(attributeToDelete);
        onDeleteClose();
        setAttributeToDelete(null);
        refetch();
      } catch (error) {
        // Error is handled in the API hook
      }
    }
  };

  const renderValue = (value: any) => {
    if (typeof value === 'object') {
      return (
        <Box>
          <Badge colorScheme='purple' size='sm' mr={2}>
            JSON
          </Badge>
          <Text fontSize='sm' color='gray.600'>
            {JSON.stringify(value)}
          </Text>
        </Box>
      );
    }

    if (typeof value === 'boolean') {
      return (
        <Box>
          <Badge colorScheme={value ? 'green' : 'red'} size='sm' mr={2}>
            {value ? 'TRUE' : 'FALSE'}
          </Badge>
        </Box>
      );
    }

    if (typeof value === 'number') {
      return (
        <Box>
          <Badge colorScheme='blue' size='sm' mr={2}>
            NUMBER
          </Badge>
          <Text fontSize='sm' color='gray.600'>
            {value}
          </Text>
        </Box>
      );
    }

    return (
      <Box>
        <Badge colorScheme='gray' size='sm' mr={2}>
          STRING
        </Badge>
        <Text fontSize='sm' color='gray.600'>
          {String(value)}
        </Text>
      </Box>
    );
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size='6xl'>
        <ModalOverlay />
        <ModalContent maxW='1200px'>
          <ModalHeader>Manage Location Attributes</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={6} align='stretch'>
              <Alert status='info'>
                <AlertIcon />
                Manage custom attributes for this location. Attributes can store additional metadata and configuration.
              </Alert>

              {/* Add/Edit Form */}
              <Box p={4} border='1px' borderColor='gray.200' borderRadius='md'>
                <VStack spacing={4} align='stretch'>
                  <Flex align='center'>
                    <Text fontWeight='bold' color='gray.700'>
                      {editingAttributeId ? 'Edit Attribute' : 'Add New Attribute'}
                    </Text>
                    <Spacer />
                    {editingAttributeId && (
                      <Button size='sm' variant='ghost' onClick={resetForm}>
                        Cancel Edit
                      </Button>
                    )}
                  </Flex>

                  <HStack spacing={4}>
                    <FormControl isRequired isInvalid={!!errors.key}>
                      <FormLabel>Key</FormLabel>
                      <Input
                        value={formData.key}
                        onChange={(e) => setFormData((prev) => ({ ...prev, key: e.target.value }))}
                        placeholder='e.g., opening_hours, special_features'
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Value Type</FormLabel>
                      <ChakraSelect
                        value={valueTypeOptions.find((option) => option.value === formData.valueCast) || null}
                        onChange={(selectedOption: any) => {
                          if (selectedOption && selectedOption.value) {
                            setFormData((prev) => ({ ...prev, valueCast: selectedOption.value }));
                          }
                        }}
                        options={valueTypeOptions}
                        placeholder='Select value type...'
                        isClearable={false}
                        isSearchable={false}
                        getOptionLabel={(option: any) => option?.label || ''}
                        getOptionValue={(option: any) => option?.value || ''}
                        formatOptionLabel={(option: any) => (
                          <Box>
                            <Text fontWeight='medium'>{option?.label || ''}</Text>
                            <Text fontSize='xs' color='gray.500'>
                              {option?.description || ''}
                            </Text>
                          </Box>
                        )}
                        closeMenuOnSelect={true}
                        blurInputOnSelect={false}
                        menuPlacement='auto'
                      />
                    </FormControl>
                  </HStack>

                  <FormControl isRequired isInvalid={!!errors.value}>
                    <FormLabel>Value</FormLabel>
                    <Input
                      value={formData.value}
                      onChange={(e) => setFormData((prev) => ({ ...prev, value: e.target.value }))}
                      placeholder={
                        formData.valueCast === 'boolean'
                          ? 'true or false'
                          : formData.valueCast === 'number'
                          ? 'Enter a number'
                          : formData.valueCast === 'json'
                          ? '{"key": "value"}'
                          : 'Enter value'
                      }
                    />
                    {errors.value && (
                      <Text fontSize='sm' color='red.500' mt={1}>
                        {errors.value}
                      </Text>
                    )}
                  </FormControl>

                  <HStack>
                    <Button
                      colorScheme='blue'
                      onClick={handleSubmit}
                      isLoading={createMutation.isLoading || updateMutation.isLoading}
                      loadingText={editingAttributeId ? 'Updating...' : 'Adding...'}
                      leftIcon={<FiPlus />}>
                      {editingAttributeId ? 'Update Attribute' : 'Add Attribute'}
                    </Button>
                  </HStack>
                </VStack>
              </Box>

              {/* Attributes List */}
              <Box>
                <Text fontWeight='bold' color='gray.700' mb={4}>
                  Current Attributes ({attributesData?.total || 0})
                </Text>

                {isLoading ? (
                  <Flex justify='center' py={8}>
                    <Spinner size='lg' />
                  </Flex>
                ) : attributesData?.attributes?.length ? (
                  <Table variant='simple' size='sm'>
                    <Thead>
                      <Tr>
                        <Th>Key</Th>
                        <Th>Value</Th>
                        <Th>Created</Th>
                        <Th>Updated</Th>
                        <Th width='100px'>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {attributesData.attributes.map((attribute) => (
                        <Tr key={attribute.id}>
                          <Td>
                            <Text fontWeight='medium'>{attribute.key}</Text>
                          </Td>
                          <Td>{renderValue(attribute.value)}</Td>
                          <Td>
                            <Text fontSize='xs' color='gray.500'>
                              {new Date(attribute.createdAt).toLocaleDateString()}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize='xs' color='gray.500'>
                              {new Date(attribute.updatedAt).toLocaleDateString()}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={1}>
                              <Tooltip label='Edit attribute'>
                                <IconButton
                                  aria-label='Edit attribute'
                                  icon={<FiEdit3 />}
                                  size='sm'
                                  colorScheme='blue'
                                  variant='outline'
                                  onClick={() => handleEdit(attribute)}
                                />
                              </Tooltip>
                              <Tooltip label='Delete attribute'>
                                <IconButton
                                  aria-label='Delete attribute'
                                  icon={<FiTrash2 />}
                                  size='sm'
                                  colorScheme='red'
                                  variant='outline'
                                  onClick={() => handleDelete(attribute.id)}
                                />
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ) : (
                  <Alert status='info'>
                    <AlertIcon />
                    No attributes found for this location. Add some attributes using the form above.
                  </Alert>
                )}
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Attribute</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align='stretch'>
              <Alert status='warning'>
                <AlertIcon />
                This action cannot be undone.
              </Alert>

              <Text>Are you sure you want to delete this attribute?</Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant='ghost' mr={3} onClick={onDeleteClose} disabled={deleteMutation.isLoading}>
              Cancel
            </Button>
            <Button
              colorScheme='red'
              onClick={confirmDelete}
              isLoading={deleteMutation.isLoading}
              loadingText='Deleting...'>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default LocationAttributesModal;
